import axios from "axios";

// Always call the API on the SAME ORIGIN as the page. The Emergent platform
// routes /api/* to the backend pod on every environment (preview + custom
// production domain), so a same-origin call avoids the cross-origin
// `Access-Control-Allow-Origin: *` + `withCredentials: true` browser block
// that surfaces as "Network Error" in axios.
//
// REACT_APP_BACKEND_URL is kept as an explicit override only (rare cases
// where the SPA is hosted on a CDN that does NOT proxy /api).
const BACKEND_URL =
  (typeof window !== "undefined" && window.location && window.location.origin) ||
  process.env.REACT_APP_BACKEND_URL ||
  "";

export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ── localStorage token fallback ──────────────────────────────────────
// Cookies are the primary session carrier, but several mobile browsers
// (privacy modes, in-app webviews like WhatsApp/Gmail) refuse to persist
// them, leaving users "logged in" (green toast) yet stuck on the login
// screen. The backend accepts `Authorization: Bearer` as a fallback, so
// we mirror the tokens into localStorage and attach them on every call.
const TOKEN_KEYS = { access: "rnv_access_token", refresh: "rnv_refresh_token", session: "rnv_session_token" };

// In-memory copy — final fallback for browsers where BOTH cookies and
// localStorage are blocked (Chrome "Block all cookies" throws on storage
// access). Keeps the session alive for the lifetime of the page.
const memTokens = { access: null, refresh: null, session: null };

export function setAuthTokens(data = {}) {
  if (data.access_token) memTokens.access = data.access_token;
  if (data.refresh_token) memTokens.refresh = data.refresh_token;
  if (data.session_token) memTokens.session = data.session_token;
  try {
    if (data.access_token) localStorage.setItem(TOKEN_KEYS.access, data.access_token);
    if (data.refresh_token) localStorage.setItem(TOKEN_KEYS.refresh, data.refresh_token);
    if (data.session_token) localStorage.setItem(TOKEN_KEYS.session, data.session_token);
  } catch { /* storage blocked — memory copy above still works */ }
}

export function clearAuthTokens() {
  memTokens.access = memTokens.refresh = memTokens.session = null;
  try { Object.values(TOKEN_KEYS).forEach((k) => localStorage.removeItem(k)); } catch {}
}

const storedBearer = () => {
  if (memTokens.access || memTokens.session) return memTokens.access || memTokens.session;
  try { return localStorage.getItem(TOKEN_KEYS.access) || localStorage.getItem(TOKEN_KEYS.session); } catch { return null; }
};

const storedRefresh = () => {
  if (memTokens.refresh) return memTokens.refresh;
  try { return localStorage.getItem(TOKEN_KEYS.refresh); } catch { return null; }
};

api.interceptors.request.use((config) => {
  const t = storedBearer();
  if (t && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Surface friendly errors + transparently refresh the access token on 401.
// On password login the access_token cookie expires in 15min while the
// refresh_token cookie lives 7 days; without this interceptor, any request
// that races past the 15-minute mark (e.g. a long voice-journal upload that
// began before the token expired) returns "Not authenticated".
let refreshInflight = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err?.config || {};
    const status = err?.response?.status;
    const url = original.url || "";
    const isAuthEndpoint = url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/logout");
    if (status === 401 && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      try {
        if (!refreshInflight) {
          // Send the refresh token in the body too — the cookie copy is
          // preferred server-side, but cookie-blocked browsers need this.
          const bodyToken = storedRefresh();
          refreshInflight = api
            .post("/auth/refresh", bodyToken ? { refresh_token: bodyToken } : {})
            .then((r) => { setAuthTokens(r?.data || {}); })
            .finally(() => { refreshInflight = null; });
        }
        await refreshInflight;
        // Drop the stale Authorization header so the request interceptor
        // re-attaches the freshly refreshed access token on retry.
        if (original.headers) delete original.headers.Authorization;
        return api(original);
      } catch (e) {
        // refresh itself failed → bubble the original 401
      }
    }
    return Promise.reject(err);
  }
);

export function fmtError(err) {
  // Permission errors deserve a user-friendly translation — `Forbidden` on
  // its own leaves the user with no path forward. We surface the action
  // they were trying to perform and tell them who to ask.
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 403) {
    if (typeof detail === "string" && detail.toLowerCase() !== "forbidden") return detail;
    return "You don't have permission for this action — ask your admin to grant access.";
  }
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((d) => (d?.msg ? d.msg : JSON.stringify(d))).join(" ");
  if (detail?.msg) return detail.msg;
  return JSON.stringify(detail);
}

export default api;
